import { applyCouponDiscount } from '@/entities/reservation'
import {
  type IPlaceIntroduction,
  type IProductGroup,
  type ISeoPlace,
} from '@/features/seoPlaceDetail/model'

import { parsePrice } from './parsePrice'
import {
  CAR_WASH_SECTION,
  ENGINE_OIL_SECTION,
  findCarWashCheapestProduct,
  getSubjectParticle,
  SECTION_NAME_BY_CODE,
  SECTION_ORDER_INDEX,
  TIRE_SECTION,
} from './placeHelpers'

const MAX_EXTRA_PRODUCTS = 2
const MAX_CARS = 3

interface IBuildIntroductionArgs {
  introduction: IPlaceIntroduction
  place: ISeoPlace
  productGroups: IProductGroup[]
}

export const buildPlaceIntroductionText = ({
  introduction,
  place,
  productGroups,
}: IBuildIntroductionArgs): string | null => {
  const { productSections, cars } = introduction
  const placeName = introduction.placeName || place.placeName || ''
  if (!placeName) return null
  const diaryCount = introduction.diaryCount ?? 0
  const address =
    place.coordinates?.streetAddress || place.coordinates?.address || ''
  const reviewCount = place.statistic?.reviewCount ?? 0
  // 매핑(productGroups) 기반으로 우선순위 판단 — 시공 이력이 있어도 매핑이 없으면 가격 텍스트를 만들 수 없음
  const hasGroup = (section: string) =>
    productGroups.some(g => g.section === section)
  const isDetailingOnly =
    hasGroup(CAR_WASH_SECTION) &&
    !hasGroup(ENGINE_OIL_SECTION) &&
    !hasGroup(TIRE_SECTION)
  const placeType = isDetailingOnly ? '자동차 디테일링 전문점' : '자동차 정비소'

  const countParts: string[] = []
  if (diaryCount > 0) {
    countParts.push(`시공 ${diaryCount.toLocaleString()}건`)
  }
  if (reviewCount > 0) {
    countParts.push(`리뷰 ${reviewCount.toLocaleString()}건`)
  }
  const subjectParticle = getSubjectParticle(placeName)
  const prefix =
    countParts.length > 0
      ? `${address}에 위치한 ${placeType} ${placeName}${subjectParticle} 마이클 앱 기준 ${countParts.join(', ')}을 보유하고 있다.`
      : `${address}에 위치한 ${placeType} ${placeName}이다.`

  // 매핑된 그룹 중 PLACE_CATEGORY_PRODUCTS에 정의된 section만 후보로. 정렬은 시공 횟수 DESC + 카테고리 우선순위 tie-break
  const diaryCountBySection = new Map<string, number>(
    productSections.map(s => [s.section, s.diaryCount ?? 0])
  )
  const sectionCodes = Array.from(
    new Set(
      productGroups.map(g => g.section).filter((s): s is string => Boolean(s))
    )
  )
    .filter(s => SECTION_ORDER_INDEX[s] !== undefined)
    .sort((a, b) => {
      const diff =
        (diaryCountBySection.get(b) ?? 0) - (diaryCountBySection.get(a) ?? 0)
      if (diff !== 0) return diff
      return (
        (SECTION_ORDER_INDEX[a] ?? Number.POSITIVE_INFINITY) -
        (SECTION_ORDER_INDEX[b] ?? Number.POSITIVE_INFINITY)
      )
    })
  const priceText = buildPriceText({
    isDetailingOnly,
    placeName,
    productSections: sectionCodes,
    productGroups,
  })
  if (!priceText) return null

  const carText = buildCarText(placeName, cars)

  return [prefix, priceText, carText].filter(Boolean).join(' ')
}

interface IBuildPriceTextArgs {
  isDetailingOnly: boolean
  placeName: string
  productSections: string[]
  productGroups: IProductGroup[]
}

const buildPriceText = ({
  isDetailingOnly,
  placeName,
  productSections,
  productGroups,
}: IBuildPriceTextArgs): string => {
  // 1순위: 엔진오일 매핑
  if (productSections.includes(ENGINE_OIL_SECTION)) {
    return buildSectionPriceText({
      placeName,
      primarySection: ENGINE_OIL_SECTION,
      primaryLabel: '엔진오일 교체',
      productSections,
      productGroups,
    })
  }

  // 2순위: 엔진오일 X, 타이어 매핑
  if (productSections.includes(TIRE_SECTION)) {
    return buildSectionPriceText({
      placeName,
      primarySection: TIRE_SECTION,
      primaryLabel: '타이어 교체',
      productSections,
      productGroups,
    })
  }

  // 3순위: 디테일링만 매핑 — car_wash group의 targetProducts 중 최저가 product의 productName 사용
  if (isDetailingOnly) {
    const cheapest = findCarWashCheapestProduct(productGroups)
    if (!cheapest) return ''
    const particle = getSubjectParticle(cheapest.name)
    return `${placeName}의 대표 시공인 ${cheapest.name}${particle} ${cheapest.price.toLocaleString()}원부터 시작한다.`
  }

  return ''
}

interface IBuildSectionPriceTextArgs {
  placeName: string
  primarySection: string
  primaryLabel: string
  productSections: string[]
  productGroups: IProductGroup[]
}

const buildSectionPriceText = ({
  placeName,
  primarySection,
  primaryLabel,
  productSections,
  productGroups,
}: IBuildSectionPriceTextArgs): string => {
  const primaryGroup = productGroups.find(g => g.section === primarySection)
  const primarySalePrice = parsePrice(
    primaryGroup?.lowestPriceTargetProduct?.salePrice
  )
  if (primarySalePrice <= 0) return ''
  const primaryCouponPrice = applyCouponDiscount(
    primarySection,
    primarySalePrice
  )

  const others = productSections
    .filter(s => s !== primarySection)
    .slice(0, MAX_EXTRA_PRODUCTS)
    .map(s => SECTION_NAME_BY_CODE[s])
    .filter((name): name is string => Boolean(name))

  // 엔진오일은 검색 노출 가격 기준을 정가로 통일하고, 쿠폰적용가를 함께 밝힌다
  if (primarySection === ENGINE_OIL_SECTION) {
    const primaryOriginalPrice = parsePrice(
      primaryGroup?.lowestPriceTargetProduct?.price
    )
    if (primaryOriginalPrice <= 0) return ''
    const priceClause = `${placeName}의 ${primaryLabel}는 ${primaryOriginalPrice.toLocaleString()}원부터이며, 앱 설치 쿠폰을 쓰면 ${primaryCouponPrice.toLocaleString()}원부터다`

    if (others.length === 0) return `${priceClause}.`
    return `${priceClause}. ${others.join(', ')}도 많이 시공한다.`
  }

  const priceClause = `${placeName}의 ${primaryLabel}는 ${primaryCouponPrice.toLocaleString()}원부터 시작`

  if (others.length === 0) return `${priceClause}한다.`
  return `${priceClause}하며, ${others.join(', ')}도 많이 시공한다.`
}

const buildCarText = (
  placeName: string,
  cars: IPlaceIntroduction['cars']
): string => {
  if (cars.length === 0) return ''
  const carNames = cars
    .slice(0, MAX_CARS)
    .map(c => [c.companyName, c.carName].filter(Boolean).join(' '))
    .filter(Boolean)
    .join(', ')
  if (!carNames) return ''
  return `${placeName}에서 가장 많이 시공한 차종은 ${carNames} 순이다.`
}
